import { useState, useEffect } from 'react';

export interface PWAInfo {
  isPWA: boolean;
  displayMode: 'fullscreen' | 'standalone' | 'minimal-ui' | 'browser';
  platform: 'ios' | 'android' | 'windows' | 'mac' | 'desktop' | 'unknown';
  canInstall: boolean;
  installPrompt: any;
}

export const usePWA = (): PWAInfo => {
  const [pwaInfo, setPWAInfo] = useState<PWAInfo>({
    isPWA: false,
    displayMode: 'browser',
    platform: 'unknown',
    canInstall: false,
    installPrompt: null
  });

  useEffect(() => {
    const updatePWAInfo = () => {
      // Detectar si es PWA
      const isStandalone = window.matchMedia('(display-mode: standalone)').matches;
      const isFullscreen = (window.navigator as any).standalone === true;
      const isPWAMode = isStandalone || isFullscreen;

      // Detectar display mode
      let displayMode: PWAInfo['displayMode'] = 'browser';
      const displayModes: PWAInfo['displayMode'][] = ['fullscreen', 'standalone', 'minimal-ui', 'browser'];
      
      for (const mode of displayModes) {
        if (window.matchMedia(`(display-mode: ${mode})`).matches) {
          displayMode = mode;
          break;
        }
      }

      // Detectar plataforma
      let platform: PWAInfo['platform'] = 'unknown';
      const userAgent = navigator.userAgent;
      
      if (/iPad|iPhone|iPod/.test(userAgent)) platform = 'ios';
      else if (/Android/.test(userAgent)) platform = 'android';
      else if (/Windows/.test(userAgent)) platform = 'windows';
      else if (/Mac/.test(userAgent)) platform = 'mac';
      else platform = 'desktop';

      // Detectar si puede instalar
      const canInstall = 'serviceWorker' in navigator && 'PushManager' in window;
      
      // Para iOS, siempre puede "instalar" (mostrar instrucciones)
      const isIOS = /iPad|iPhone|iPod/.test(userAgent);
      const canInstallOrIOS = canInstall || isIOS;

      setPWAInfo({
        isPWA: isPWAMode,
        displayMode,
        platform,
        canInstall: canInstallOrIOS,
        installPrompt: null
      });
    };

    updatePWAInfo();

    // Escuchar evento de instalación
    let installPrompt: any = null;

    const handleBeforeInstallPrompt = (e: any) => {
      e.preventDefault();
      installPrompt = e;
      setPWAInfo(prev => ({ ...prev, installPrompt: e }));
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);

    // Escuchar cambios en display mode
    const mediaQueries = (['fullscreen', 'standalone', 'minimal-ui', 'browser'] as const).map(mode => {
      const mq = window.matchMedia(`(display-mode: ${mode})`);
      mq.addListener(updatePWAInfo);
      return mq;
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      mediaQueries.forEach(mq => mq.removeListener(updatePWAInfo));
    };
  }, []);

  return pwaInfo;
};

// Hook específico para mostrar el botón de instalación
export const useInstallPrompt = () => {
  const { installPrompt, isPWA, canInstall, platform } = usePWA();
  const [isInstallable, setIsInstallable] = useState(false);

  useEffect(() => {
    // Para iOS, mostrar si no es PWA y es iOS
    // Para otros navegadores, requiere el installPrompt
    const isIOSInstallable = platform === 'ios' && !isPWA;
    const isRegularInstallable = !isPWA && canInstall && !!installPrompt;
    
    setIsInstallable(isIOSInstallable || isRegularInstallable);
  }, [isPWA, canInstall, installPrompt, platform]);

  const promptInstall = async () => {
    // Si es iOS, no hay prompt nativo, solo devolver true para mostrar instrucciones
    if (platform === 'ios') {
      return true;
    }

    if (!installPrompt) return false;

    try {
      const result = await installPrompt.prompt();
      const choice = await result.userChoice;
      
      if (choice === 'accepted') {
        return true;
      }
      return false;
    } catch (error) {
      return false;
    }
  };

  return {
    isInstallable,
    promptInstall
  };
};